import React, {
  useEffect,
  useState,
} from 'react'
import { useSSE } from 'use-sse'
import reframe from 'reframe.js'

import useClientSideState from 'hooks/useClientSideState'

import {
  get,
  ApiResponse,
} from 'utils/apis/insuranceLounge'

import Http404 from 'screens/404'
import Http500 from 'screens/500'

import {
  Post,
  Tag,
} from 'screens/Blog/types'

import { OnPostsLoaded } from 'components/blocks/Blog/PostsGrid/types'

import {
  ApiData,
  ContainerProps,
} from './types'

import BlogPost from './index.presentational'

const BlogPostContainer: React.FC<ContainerProps> = ({
  slug,
  uuid,
}): React.ReactElement => {
  const [
    isLoading,
    setIsLoading,
  ] = useClientSideState<boolean>(false)

  const [
    showRelatedPosts,
    setShowRelatedPosts,
  ] = useState<boolean>(true)

  const onRelatedPostsLoaded: OnPostsLoaded = posts => {
    setShowRelatedPosts(!!posts?.length)
  }

  const [
    data,
    error,
  ] = useSSE((): Promise<ApiData> => {
    setIsLoading(true)

    const uri = slug ? `/blog/posts/${slug}` : `/blog/posts/preview/${uuid}`

    return get(uri)
      .then((response: ApiResponse): ApiData => {
        const post = response.status === 200 ? response.data as Post : undefined

        const tagSlugs = (post?.tags || []).reduce((acc: string, tag: Tag) => {
          acc = `${acc},${tag.slug}`

          return acc
        }, '').replace(/^,/, '')

        return {
          post,
          tagSlugs,
        } as ApiData
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    slug,
    uuid,
  ])

  // Make videos responsive
  useEffect(() => {
    reframe('iframe.ql-video[src*="youtube.com"],iframe.ql-video[src*="vimeo.com"]')
  }, [ data ])

  if (error) {
    return React.createElement(Http500)
  }

  const {
    post,
    tagSlugs,
  } = (data || {})

  if (!isLoading && !post) {
    return React.createElement(Http404)
  }

  return React.createElement(BlogPost, {
    isLoading,
    onRelatedPostsLoaded,
    post,
    showRelatedPosts,
    tagSlugs,
  })
}

export default BlogPostContainer
